import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Question } from '../../schemas/schemas';
import { X, Trash2 } from 'lucide-react';

interface InlineQuestionEditorProps {
  question: Question;
  onSave: (question: Question) => void;
  onCancel: () => void;
  onDelete?: (questionId: string) => void;
}

export default function InlineQuestionEditor({ 
  question, 
  onSave, 
  onCancel, 
  onDelete 
}: InlineQuestionEditorProps) {
  // Get the initial question type from the editing question or default to multiple choice
  const initialType = question?.question_type || 'multiple_choice';
  const [questionType, setQuestionType] = useState<string>(initialType);
  
  // Get options for multiple choice questions 
  const initialOptions = (): string[] => {
    if (question && question.question_type === 'multiple_choice') {
      return question.options || ['', ''];
    }
    return ['', ''];
  };
  
  const [options, setOptions] = useState<string[]>(initialOptions());
  
  const isEditing = !!question;
  
  // Handle form submission
  const handleSave = () => {
    // Create a new question or modify the existing one
    let newQuestion: Question;
    
    // Generate a random ID if not editing
    const questionId = question?.question_id || `q${Date.now()}`;
    const text = document.getElementById('question-text') as HTMLTextAreaElement;
    
    // This is simplified - in a real app, we would use proper form handling
    if (questionType === 'multiple_choice') {
      // Get the correct option from radio selection
      const correctOptionRadio = document.querySelector('input[name="correct-answer"]:checked') as HTMLInputElement;
      const correctOption = correctOptionRadio ? correctOptionRadio.value : options[0] || 'Option A';
      
      newQuestion = {
        question_id: questionId,
        question_text: text.value || 'Sample multiple choice question',
        question_type: 'multiple_choice',
        options: options.length ? options : ['Option A', 'Option B', 'Option C'],
        correct_option: correctOption,
      };
    } else if (questionType === 'boolean') {
      // Get the selected boolean value
      const trueRadio = document.getElementById('true-answer') as HTMLInputElement;
      
      newQuestion = {
        question_id: questionId,
        question_text: text.value || 'Sample true/false question',
        question_type: 'boolean',
        correct_answer: trueRadio.checked ? 'true' : 'false',
      };
    } else {
      // Must be a text question
      const textAnswer = document.getElementById('text-answer') as HTMLInputElement;
      
      newQuestion = {
        question_id: questionId,
        question_text: text.value || 'Sample text input question',
        question_type: 'text',
        correct_answer: textAnswer.value || '',
      };
    }
    
    onSave(newQuestion);
  };

  
  return (
    <Card className="w-full border-2 border-blue-200 bg-blue-50 dark:bg-blue-900/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">
              {isEditing ? 'Chỉnh sửa câu hỏi' : 'Thêm câu hỏi mới'}
            </CardTitle>
            <CardDescription>
              {isEditing ? 'Chỉnh sửa câu hỏi đã chọn' : 'Tạo câu hỏi mới cho bài kiểm tra này'}
            </CardDescription>
          </div>
          <div className="flex items-center space-x-2">
            {onDelete && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onDelete(question.question_id)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCancel}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <label htmlFor="question-text" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Nội dung câu hỏi
            </label>
            <textarea
              id="question-text"
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
              placeholder="Nhập nội dung câu hỏi"
              defaultValue={question?.question_text || ''}
            ></textarea>
          </div>
          
          <div>
            <label htmlFor="question-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Loại câu hỏi
            </label>
            <select
              id="question-type"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
              defaultValue={initialType}
              onChange={(e) => setQuestionType(e.target.value)}
            >
              <option value="multiple_choice">Trắc nghiệm</option>
              <option value="boolean">Đúng/Sai</option>
              <option value="text">Trả lời ngắn</option>
              <option value="fill_in_blank">Điền vào chỗ trống</option>
            </select>
          </div>
          
          {questionType === 'multiple_choice' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Các lựa chọn
              </label>
              <div className="mt-2 space-y-2">
                {options.map((option, index) => (
                  <div key={index} className="flex items-center">
                    <input
                      type="text"
                      className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                      placeholder={`Lựa chọn ${index + 1}`}
                      defaultValue={option}
                      onChange={(e) => {
                        const newOptions = [...options];
                        newOptions[index] = e.target.value;
                        setOptions(newOptions);
                      }}
                    />
                    <div className="ml-2">
                      <input
                        type="radio"
                        name="correct-answer"
                        value={option}
                        className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                        defaultChecked={question?.question_type === 'multiple_choice' && question.correct_option === option}
                      />
                      <label className="ml-1 text-xs text-gray-500">Đúng</label>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="ml-2 text-red-500"
                      onClick={() => {
                        if (options.length > 2) {
                          setOptions(options.filter((_, i) => i !== index));
                        }
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </Button>
                  </div>
                ))}
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setOptions([...options, ''])}
                >
                  Thêm lựa chọn
                </Button>
              </div>
            </div>
          )}
          
          {questionType === 'boolean' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Đáp án đúng
              </label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center">
                  <input
                    id="true-answer"
                    name="boolean-answer"
                    type="radio"
                    className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    defaultChecked={question?.correct_answer === 'true'}
                  />
                  <label htmlFor="true-answer" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Đúng
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="false-answer"
                    name="boolean-answer"
                    type="radio"
                    className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    defaultChecked={question?.correct_answer === 'false'}
                  />
                  <label htmlFor="false-answer" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Sai
                  </label>
                </div>
              </div>
            </div>
          )}
          
          {(questionType === 'text' || questionType === 'fill_in_blank') && (
            <div>
              <label htmlFor="text-answer" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Đáp án đúng
              </label>
              <input
                id="text-answer"
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                placeholder="Nhập đáp án đúng"
                defaultValue={question?.correct_answer || ''}
              />
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-end space-x-2">
        <Button variant="outline" onClick={onCancel}>
          Hủy
        </Button>
        <Button onClick={handleSave}>
          {isEditing ? 'Lưu thay đổi' : 'Lưu'}
        </Button>
      </CardFooter>
    </Card>
  );
}
